import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { perf } from '../lib/perfCollector';
import { terminalManager } from '../lib/terminalManager';
import { useSettings } from '../stores/settingsStore';
import { useTerminalRuntime } from '../stores/terminalRuntimeStore';
import { useTerminalWorkspace } from '../stores/terminalWorkspaceStore';
import { Sparkline } from './Sparkline';

interface ProcessMetric {
    type: string;
    pid: number;
    cpuPercent: number;
    memoryKB: number;
}

function fmtBytesPerSec(n: number): string {
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB/s`;
    if (n >= 1024) return `${(n / 1024).toFixed(1)} KB/s`;
    return `${Math.round(n)} B/s`;
}

const fmtMs = (ms: number | null): string =>
    ms === null ? '—' : `${Math.round(ms)} ms`;

const ROW = 'flex items-center gap-2 leading-5';
const LABEL = 'w-9 shrink-0 text-text-faint';
const VALUE = 'w-16 shrink-0 text-text-dim tabular-nums';

function MetricRow({
    label,
    value,
    data,
    color,
}: {
    label: string;
    value: string;
    data?: number[];
    color?: string;
}) {
    return (
        <div className={ROW}>
            <span className={LABEL}>{label}</span>
            <span className={VALUE}>{value}</span>
            <div className="flex-1 min-w-0">
                {data && data.length > 1 && (
                    <Sparkline data={data} height={16} color={color} />
                )}
            </div>
        </div>
    );
}

/** Floating performance readout (RTT, throughput, echo latency, FPS, memory).
 *  All sampling loops live in this component's effects, so collection beyond
 *  cheap counters only happens while the HUD is open. */
export function PerfHud() {
    const enabled = useSettings((s) => s.perfHudEnabled);
    const setPerfHudEnabled = useSettings((s) => s.setPerfHudEnabled);
    return enabled ? <PerfHudPanel onClose={() => setPerfHudEnabled(false)} /> : null;
}

function PerfHudPanel({ onClose }: { onClose: () => void }) {
    const activePaneId = useTerminalWorkspace(
        (s) => s.workspace?.activePaneId ?? null,
    );
    const conn = useTerminalRuntime((s) =>
        activePaneId ? s.conns[activePaneId] : undefined,
    );

    const [, setTick] = useState(0);
    const [processes, setProcesses] = useState<ProcessMetric[] | null>(null);
    const activeRef = useRef(activePaneId);
    activeRef.current = activePaneId;

    useEffect(() => {
        perf.startFps();
        let n = 0;
        let metricsInFlight = false;
        const timer = setInterval(() => {
            perf.sample();
            n += 1;
            // RTT probe for the active session every other tick (~2s).
            if (n % 2 === 0 && activeRef.current) {
                terminalManager.sendPing(activeRef.current);
            }
            // Desktop-only per-process CPU/memory via the Electron bridge.
            const appMetrics = window.cotty?.appMetrics;
            if (appMetrics && !metricsInFlight) {
                metricsInFlight = true;
                appMetrics()
                    .then((m) => setProcesses(m.processes))
                    .catch(() => {})
                    .finally(() => {
                        metricsInFlight = false;
                    });
            }
            setTick((t) => t + 1);
        }, 1000);
        return () => {
            clearInterval(timer);
            perf.stopFps();
        };
    }, []);

    const snap = perf.snapshot(activePaneId);
    const s = snap.session;

    const procLine = processes
        ?.filter((p) => ['Browser', 'Tab', 'GPU'].includes(p.type))
        .map(
            (p) =>
                `${p.type === 'Browser' ? 'main' : p.type === 'Tab' ? 'rend' : 'gpu'} ${p.cpuPercent.toFixed(0)}% ${Math.round(p.memoryKB / 1024)}MB`,
        )
        .join(' · ');

    return (
        // Bottom-right over the terminal surface — bottom-left would cover the
        // nav rail's settings/collapse/user-menu buttons. z below modals (100)
        // and toasts (200): toasts briefly overlap the readout, which beats
        // blocking interactive chrome.
        <div className="fixed bottom-4 right-4 z-90 pointer-events-none">
            <div className="pointer-events-auto w-72 px-3 py-2.5 bg-bg-1/95 border border-border rounded-lg shadow-(--shadow-2) font-mono text-[11px] backdrop-blur-sm">
                <div className="flex items-center gap-2 pb-1.5 mb-1.5 border-b border-border-faint">
                    <span className="text-[10px] font-semibold tracking-[0.12em] text-accent">
                        PERF
                    </span>
                    {s && conn && (
                        <span className="min-w-0 truncate text-text-muted">
                            {conn.status} · {s.renderer}
                            {conn.cols > 0 && ` · ${conn.cols}×${conn.rows}`}
                        </span>
                    )}
                    <button
                        type="button"
                        aria-label="Close performance HUD"
                        onClick={onClose}
                        className="ml-auto grid w-5 h-5 place-items-center rounded text-text-faint hover:text-text hover:bg-bg-hover"
                    >
                        <X size={11} />
                    </button>
                </div>

                {s && (
                    <div className="flex flex-col pb-1.5 mb-1.5 border-b border-border-faint">
                        <MetricRow label="rtt" value={fmtMs(s.rttLast)} data={s.rtt} />
                        <MetricRow
                            label="in"
                            value={fmtBytesPerSec(s.inRateLast)}
                            data={s.inRate}
                            color="var(--success)"
                        />
                        <MetricRow
                            label="out"
                            value={fmtBytesPerSec(s.outRateLast)}
                            data={s.outRate}
                            color="var(--warning)"
                        />
                        <MetricRow label="echo" value={fmtMs(s.echoLast)} />
                    </div>
                )}

                <div className="flex flex-col">
                    <MetricRow
                        label="fps"
                        value={snap.fpsLast === null ? '—' : String(Math.round(snap.fpsLast))}
                        data={snap.fps}
                        color="var(--accent-2)"
                    />
                    {snap.heapLast !== null && (
                        <MetricRow
                            label="heap"
                            value={`${Math.round(snap.heapLast)} MB`}
                            data={snap.heap}
                            color="#7c5cff"
                        />
                    )}
                    <div className={ROW}>
                        <span className={LABEL}>term</span>
                        <span className="text-text-dim tabular-nums">
                            {snap.sessionCount} live
                            {snap.sessionCount > 1 &&
                                ` · Σ in ${fmtBytesPerSec(snap.aggInRate)}`}
                        </span>
                    </div>
                    {procLine && (
                        <div className={`${ROW} text-text-muted truncate`}>{procLine}</div>
                    )}
                </div>
            </div>
        </div>
    );
}
